<template>
    <v-card>
        <v-card-title>
            <h3 class="q-title">{{ datatable.title }}</h3>
            <v-spacer v-if="datatable.title"></v-spacer>
            <v-text-field
                v-model="search"
                :disabled="isDemoMode"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
            ></v-text-field>
        </v-card-title>

        <v-data-table 
            :headers="the_headers" 
            :items="all_data"
            item-key="id"
            class="elevation-0"
            :footer-props="{itemsPerPageOptions: [10,25,50,100]}" 
            :loading="loading" 
            :options.sync="options" 
            :search="search"
            :no-data-text="noDataText"
        ></v-data-table>
    </v-card>
</template>

<script>
import QuilityAPI from '@/store/API/QuilityAPI.js'

export default {
    name: "DataTable",
    props: {
        datatable: {
            type: Object,
            default() {
                return {
                    title: null,
                    csvUrl: null,
                    filterOnAgentCode: null,
                    rankOnColumnName: null,
                    rankSortDirection: null
                }
            }
        }
    },
    data: () => ({
        isLoading: false,
        headers: [],
        search: '',
        options: {
            sortBy: [], //used to set the default sorting column
            sortDesc: [],
            itemsPerPage: 25
        },
        the_data: [],
        error: ""
    }),
    async mounted() {
        await this.loadData()
        this.initOptions();
    },
    computed: {
        loading() {
            return this.isLoading;
        },
        the_headers() {
            if (!this.headers?.length) return []

            const headers = this.headers.includes('Rank')
                ? this.headers
                : ['Rank', ...this.headers]

            return headers.map((header) => ({
                text: header,
                value: header,
            }))
        },
        all_data() {
            let data = this.the_data

            if (this.datatable.rankOnColumnName) {
                data = [...this.the_data].sort((a, b) => {
                    const columnName = this.datatable.rankOnColumnName.trim()

                    if (!a[columnName]) return 1;

                    if (typeof a[columnName] === "string" && typeof b[columnName] === "string") {
                        const numberA = parseFloat(a[columnName].replaceAll(',', '').replaceAll('$', ''))
                        const numberB = parseFloat(b[columnName].replaceAll(',', '').replaceAll('$', ''))
                        if (!isNaN(numberA) && !isNaN(numberB)) {
                            return numberB - numberA
                        }
                    }

                    return a[columnName] < b[columnName] ? 1 : -1
                })
            }

            const hasRankProperty = data.length > 0 && 'Rank' in data[0]

            if (hasRankProperty) return data

            return data.map((item, index) => ({
                ...item,
                Rank: index + 1,
            }))
        },
        noDataText() {
            return this.error != "" ? this.error : "No data available"
        },
    },
    methods: {
        loadData() {
            if (!this.datatable.csvUrl) return
            this.isLoading = true
            
            return QuilityAPI.getCsvData(this.datatable.csvUrl, {
                convertToJson: true, 
                filterOnAgentCode: this.datatable.filterOnAgentCode,
                rankOnColumnName: this.datatable.rankOnColumnName,
                rankSortDirection: this.datatable.rankSortDirection
            })
                .then((json) => {
                    this.isLoading = false
                    if (!json) {
                        this.showError("Incorrect format.");
                        return
                    }
                    if (json?.response?.clientError) {
                        this.showError(json.response.body.message);
                        return
                    }
                    if (json.isDemoMode) {
                        this.showError(json.msg);
                        return
                    }
                    
                    this.headers = json.headers.map((header) => header.trim())
                    this.the_data = (json.data || []).map((item) => {
                        const entries = Object.entries(item).map(([keys, value]) => [keys.trim(), value])
                        return Object.fromEntries(entries)
                    })
                    this.error = json.error
                }).catch((err) => {
                    this.isLoading = false
                    if (err.status == 431) {
                        this.showError(err + " You probably need to clear all your cookie and session variables and login again.")
                        return
                    }
                    this.showError(err.message);
                });
        },
        initOptions() {
            this.options.sortBy = this.datatable.rankOnColumnName
                ? [this.datatable.rankOnColumnName.trim()]
                : []
            const isDescending = this.datatable.rankSortDirection === 'Descending' || this.datatable.rankSortDirection == null
            this.options.sortDesc = [isDescending]
        },
    },
    watch: {
        'datatable.csvUrl'(newValue) {
            if (!newValue) {
                this.the_data = []
                this.headers = []
                this.error = ""
                return
            }

            this.loadData()
        },
        'datatable.filterOnAgentCode'() {
            this.loadData()
        },
        datatable: {
            handler() {
                this.initOptions();
            },
            deep: true,
        }
    },
}
</script>
